import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Calculator, TrendingUp } from "lucide-react";
import { calculatePresentValueGoalSeek } from "../../lib/noa-calculations";
import { parseLocalDateInput } from "../../lib/dateFormat";

interface Props {
  totalPV: string;
  lines: any[];
  discountRate: number;
  assignmentDate: string;
  lenderFundsGST: boolean;
  lenderFundsTDS?: boolean;
  children: React.ReactNode;
}

export default function PVCalculationModal({ 
  totalPV,
  lines, 
  discountRate, 
  assignmentDate, 
  lenderFundsGST,
  lenderFundsTDS = false,
  children 
}: Props) {
  const [open, setOpen] = useState(false);

  // Use canonical Goal Seek PV calculation
  const pvResult = calculatePresentValueGoalSeek(lines, lenderFundsGST, discountRate, parseLocalDateInput(assignmentDate), lenderFundsTDS);
  const pvRows = pvResult.pvRows;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="h-[85vh] md:max-w-6xl max-h-[85vh] ms-auto w-[90%] rounded-t-xl">
        <DialogHeader>Selected Rental Periods
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Present Value Calculation Details
          </DialogTitle>
          <DialogDescription>
            Structured PV calculation with Goal Seek methodology
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total PV</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">₹{totalPV}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Discount Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">{discountRate}% p.a.</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Funding Mode</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">
                  {lenderFundsGST ? "Base + GST" : "Base Only"}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Periods</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold">{lines.length}</div>
              </CardContent>
            </Card>
          </div>

          {/* PV Calculation Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                PV Calculation Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-64">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sr.No</TableHead>
                      <TableHead>From Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Factor (Days)</TableHead>
                      <TableHead className="text-right">Due Amount</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Principal O/S</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pvRows.map((row) => (
                      <TableRow key={row.srNo}>
                        <TableCell>{row.srNo}</TableCell>
                        <TableCell>{row.fromDate}</TableCell>
                        <TableCell>{row.dueDate}</TableCell>
                        <TableCell>{row.factor}</TableCell>
                        <TableCell className="text-right">₹{row.due?.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right">₹{Math.round(row.interest)?.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right">₹{Math.round(row.principal)?.toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-right">₹{Math.round(row.principalOS)?.toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          <div className="text-xs text-muted-foreground">
            * Goal Seek methodology: Last "Principal Outstanding" is set to 0 (±1 tolerance) by adjusting PV of Rentals
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}